import { useCallback, useEffect, useRef, useState } from 'react';
import {
  ProposalResponse,
  analyzeProposal,
  getProposal,
  updateProposalStep,
} from 'services/actions/ai_management_proposal';
import {
  PROPOSAL_ANALYSIS_TIMED_OUT,
  Proposal,
  ProposalStep,
  toProposal,
} from 'services/ai_management_proposal';

interface State {
  id: number | null;
  proposal: Proposal | null;
  error: boolean;
}

// Mesmo ritmo e prazo do Analista de Habilitação: cada consulta sincroniza a análise no bw-backend.
export const PROPOSAL_POLL_DELAYS = [3000, 5000, 10000, 15000];
export const PROPOSAL_POLL_LIMIT = 10 * 60 * 1000;

const emptyState = (id: number | null, error: boolean): State => ({ id, proposal: null, error });

const isHidden = () => typeof document !== 'undefined' && document.hidden;

const isProposalResponse = (data: unknown) =>
  !!data && typeof data === 'object' && typeof (data as { status?: unknown }).status === 'string';

export function useProposal(biddingId: number | null) {
  const [state, setState] = useState<State>(() => emptyState(null, false));
  const [analyzing, setAnalyzing] = useState(false);
  const [hidden, setHidden] = useState(isHidden);
  const [tick, setTick] = useState(0);

  // Licitação atual, para descartar resposta que chega depois de trocar de licitação.
  const biddingRef = useRef(biddingId);
  biddingRef.current = biddingId;
  const resumeRef = useRef(false);
  const pollRef = useRef({ biddingId: null as number | null, attempt: 0, startedAt: 0 });

  const restartPolling = useCallback((id: number) => {
    pollRef.current = { biddingId: id, attempt: 0, startedAt: Date.now() };
    resumeRef.current = false;
  }, []);

  useEffect(() => {
    if (!biddingId) {
      setState(emptyState(null, false));
      return undefined;
    }

    let active = true;

    getProposal(biddingId)
      .then((response) => {
        if (!active) return;
        if (!isProposalResponse(response?.data)) {
          setState(emptyState(biddingId, true));
          return;
        }
        restartPolling(biddingId);
        setState({ id: biddingId, proposal: toProposal(response.data), error: false });
      })
      .catch((err) => {
        console.error(`Erro ao consultar a proposta da licitacao ${biddingId}:`, err);
        if (active) setState(emptyState(biddingId, true));
      });

    return () => {
      active = false;
    };
  }, [biddingId, restartPolling]);

  useEffect(() => {
    const onVisibilityChange = () => {
      if (!document.hidden) resumeRef.current = true;
      setHidden(document.hidden);
    };

    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => document.removeEventListener('visibilitychange', onVisibilityChange);
  }, []);

  const settled = state.id === biddingId;
  const processing = settled && state.proposal?.analysis?.status === 'processing';

  useEffect(() => {
    if (!processing || !biddingId || hidden) return undefined;

    if (pollRef.current.biddingId !== biddingId) restartPolling(biddingId);
    const poll = pollRef.current;

    if (Date.now() - poll.startedAt >= PROPOSAL_POLL_LIMIT) {
      setState((prev) =>
        prev.id === biddingId && prev.proposal
          ? { ...prev, proposal: { ...prev.proposal, analysis: PROPOSAL_ANALYSIS_TIMED_OUT } }
          : prev,
      );
      return undefined;
    }

    const delay = resumeRef.current
      ? 0
      : PROPOSAL_POLL_DELAYS[Math.min(poll.attempt, PROPOSAL_POLL_DELAYS.length - 1)];
    resumeRef.current = false;
    let active = true;

    const timer = setTimeout(() => {
      poll.attempt += 1;

      getProposal(biddingId)
        .then((response) => {
          if (!active || !isProposalResponse(response?.data)) return;
          setState({ id: biddingId, proposal: toProposal(response.data), error: false });
        })
        .catch((err) => {
          console.error(`Erro ao consultar a análise da proposta da licitacao ${biddingId}:`, err);
        })
        .finally(() => {
          if (active) setTick((value) => value + 1);
        });
    }, delay);

    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [processing, biddingId, hidden, tick, restartPolling]);

  const analyze = useCallback(() => {
    if (!biddingId) return Promise.reject(new Error('Licitação não carregada.'));

    const id = biddingId;
    setAnalyzing(true);
    return analyzeProposal(id)
      .then(({ data }) => {
        if (biddingRef.current !== id || !isProposalResponse(data)) return;
        restartPolling(id);
        setState({ id, proposal: toProposal(data), error: false });
      })
      .finally(() => setAnalyzing(false));
  }, [biddingId, restartPolling]);

  // Etapa em que o cliente está, gravada no servidor para outro usuário ou computador retomar dali.
  // Falha na gravação não interrompe a navegação nem aparece para o cliente.
  const saveStep = useCallback(
    (step: ProposalStep) => {
      if (!biddingId) return;
      updateProposalStep(biddingId, step).catch((err) => {
        console.error(`Erro ao gravar a etapa da proposta da licitacao ${biddingId}:`, err);
      });
    },
    [biddingId],
  );

  // Aplica a resposta da gravação sem esperar nova consulta: a casca destrava as etapas na hora.
  const applyProposal = useCallback((id: number, data: ProposalResponse) => {
    if (biddingRef.current !== id || !isProposalResponse(data)) return;
    setState({ id, proposal: toProposal(data), error: false });
  }, []);

  return {
    proposal: settled ? state.proposal : null,
    analyzing,
    loading: !!biddingId && !settled,
    error: settled && state.error,
    analyze,
    saveStep,
    applyProposal,
  };
}
